import { REWARD_RULE } from "@cede/types";

// Campaign Type Configurations
export const CAMPAIGN_TYPE_CONFIGS = {
  TRADING: "TRADING",
  HOLD_TO_EARN: "HOLD_TO_EARN",
} as const;

// Hold Campaign Status Constants
export const HOLD_CAMPAIGN_STATUS = {
  LIVE: "live",
  FUTURE: "future",
  PAST: "past",
  PAUSED: "paused",
} as const;

// Reward Rule Labels
export const HOLD_REWARD_RULE_LABELS = {
  [REWARD_RULE.PROPORTIONAL]: "Pro-rata",
  [REWARD_RULE.FIXED_SPOT]: "Fixed Spot",
} as const;

// Reward Rule Badges
export const HOLD_REWARD_RULE_BADGES = {
  [REWARD_RULE.PROPORTIONAL]: {
    text: "Daily pro-rata pool",
    className: "px-2 py-1 bg-cede-primary/20 text-cede-primary rounded-full text-xs",
  },
  [REWARD_RULE.FIXED_SPOT]: {
    text: "Fixed spot-based rewards",
    className: "px-2 py-1 bg-orange-500/20 text-orange-400 rounded-full text-xs",
  },
} as const;

/**
 * Format epoch duration to human readable string
 */
export const formatEpochDuration = (epochDuration: number): string => {
  if (epochDuration >= 24 && epochDuration % 24 === 0) {
    const days = epochDuration / 24;
    return days === 1 ? "1 day" : `${days} days`;
  } else {
    return `${epochDuration} hours`;
  }
};

/**
 * Get campaign rule descriptions with dynamic epoch duration
 */
export const getHoldRewardRuleDescriptions = (
  rewardRule: REWARD_RULE,
  epochDuration: number,
  formattedBuyInAmountTarget: string | null,
) => {
  const formattedDuration = formatEpochDuration(epochDuration);

  const descriptions = {
    [REWARD_RULE.PROPORTIONAL]: [
      {
        emoji: "💸",
        text: "Daily rewards are distributed **proportionally to each user's time-weighted holding size**.",
      },
      ...(formattedBuyInAmountTarget == null
        ? []
        : [
            {
              emoji: "🎯",
              text: `**Pool unlock:** the full epoch reward unlocks once **${formattedBuyInAmountTarget}** is held across all participants. Below that, each hour pays out only the held share of the target: half the target held pays half the reward.`,
            },
          ]),
      {
        emoji: "📅",
        text: `**Epoch:** ${formattedDuration} with **hourly snapshots**.`,
      },
    ],
    [REWARD_RULE.FIXED_SPOT]: [
      {
        emoji: "",
        text: "A predefined number of **reward spots** is available, each with a **fixed reward per spot**.",
      },
      {
        emoji: "",
        text: "Spots are allocated **first-come, first-served**: a slot is locked when a user **opts in and buys the required amount through our widget**.",
      },
      {
        emoji: "",
        text: "If a holder's balance **falls below the minimum** at any snapshot, the wallet is **automatically opted out**, the spot is vacated, and the **earliest wait-listed eligible** wallet is promoted.",
      },
      {
        emoji: "",
        text: "The spot's reward is paid **pro-rata to the time the requirement is met** within the epoch.",
      },
      {
        emoji: "📅",
        text: `**Epoch:** ${formattedDuration} with **hourly snapshots**.`,
      },
    ],
  };

  return descriptions[rewardRule];
};

// How a leaderboard record's prize accrues across the epoch's hourly snapshots
export const HOLD_PRIZE_COMPUTATION = {
  [REWARD_RULE.FIXED_SPOT]: {
    accrual:
      "Every hour, a snapshot checks your balance. Each hour you hold at least the minimum counts as an eligible hour.",
    formula: "Prize = Epoch reward ÷ spots ÷ epoch hours × your eligible hours",
  },
  [REWARD_RULE.PROPORTIONAL]: {
    accrual:
      "Every hour, a snapshot checks your balance. That hour's share of the pool (Epoch reward ÷ epoch hours) is split between eligible holders, pro-rata to their balance.",
    formula: "Prize = the sum of your hourly shares",
  },
} as const;

// Important Notes
export const HOLD_CAMPAIGN_IMPORTANT_NOTES = {
  COMMON: [
    "Tokens must be purchased through the campaign widget to be eligible.",
    "Hourly snapshots are taken to verify holding requirements.",
    "Rewards are distributed at the end of each epoch.",
    "You must not sell tokens before the end of the epoch to remain eligible for rewards.",
  ],
  [REWARD_RULE.FIXED_SPOT]: ["Fixed spot holders who fall below minimum balance lose their spot immediately."],
} as const;
